import { Observable, Subject } from 'rxjs';

import { TProfileEdgeCrease } from './t-profile-edge-crease';
import { EProfilePaintSide } from './e-profile-paint-side';
import { ProfileLine } from './profile-line';
import { profileLoadV1 } from './profile-load-v1';
import { profileLoadV2 } from './profile-load-v2';
import { profileLoadV3 } from './profile-load-v3';
import { profileToStringV1 } from './profile-to-string-v1';
import { profileToStringV2 } from './profile-to-string-v2';
import { profileToStringV3 } from './profile-to-string-v3';
import { recalculateStartLineAngle, calculateLengthTotal } from './profile-utils';
import { normalizeTemplateName } from './normalize-template-name';

export type ProfileVersion = 'v1' | 'v2' | 'v3';

export class Profile {
    private readonly profileChanges = new Subject<void>();

    public readonly changes$: Observable<void> = this.profileChanges.asObservable();
    public lines: ProfileLine[] = [];
    public edgeCreaseSizeMM = 15;
    public creaseLeft: TProfileEdgeCrease = 0;
    public creaseRight: TProfileEdgeCrease = 0;
    public paintSide: EProfilePaintSide = 1;

    private templateNameValue?: string;

    constructor(profileData?: string) {
        if (profileData) {
            this.load(profileData);
        }
    }

    public get profileTemplateName(): string | undefined {
        return this.templateNameValue;
    }

    public set profileTemplateName(value: string | undefined) {
        this.templateNameValue = normalizeTemplateName(value);
    }

    public propagateChanges(): void {
        this.profileChanges.next();
    }

    public clear(propagateChanges = true): void {
        this.lines.length = 0;
        this.edgeCreaseSizeMM = this.edgeCreaseSizeMM || 15;
        this.creaseLeft = 0;
        this.creaseRight = 0;
        this.paintSide = 1;
        this.templateNameValue = undefined;

        if (propagateChanges) {
            this.propagateChanges();
        }
    }

    public load(profileData: string): void {
        this.clear(false);

        if (!profileData) {
            this.propagateChanges();
            return;
        }

        // Auto-detect version from data
        switch (true) {
            case profileData.startsWith('v3'):
                profileLoadV3(this, profileData);
                break;
            case profileData.startsWith('v2'):
                profileLoadV2(this, profileData);
                break;
            default:
                profileLoadV1(this, profileData);
        }

        this.propagateChanges();
    }

    public rotate(angleDegree: number): void {
        if (this.lines.length) {
            const firstLine = this.lines[0];
            const startLineAngle = recalculateStartLineAngle(firstLine.alpha, firstLine.direction, angleDegree);
            firstLine.alpha = startLineAngle.angle;
            firstLine.direction = startLineAngle.direction;
            this.propagateChanges();
        }
    }

    public toString(targetVersion: ProfileVersion = 'v3'): string {
        switch (targetVersion) {
            case 'v1':
                return profileToStringV1(this);
            case 'v2':
                return profileToStringV2(this);
            default:
                return profileToStringV3(this);
        }
    }

    public lengthTotal(): number {
        return calculateLengthTotal(this);
    }

    /**
     * It's necessary for drawing in a coordinate system with an inverted Y-axis.
     * Essentially, Canvas Y-axis has an opposite direction with the 3D axis.
     */
    public inverse(): void {
        this.creaseLeft *= this.creaseLeft !== 0 ? -1 : 0;
        this.creaseRight *= this.creaseRight !== 0 ? -1 : 0;
        this.lines.forEach(line => {
            line.alpha *= -1;
            line.direction *= -1;
        });
        if (this.paintSide === EProfilePaintSide.paintTop) {
            this.paintSide = EProfilePaintSide.paintBottom;
        } else if (this.paintSide === EProfilePaintSide.paintBottom) {
            this.paintSide = EProfilePaintSide.paintTop;
        }
        this.propagateChanges();
    }
}
